// src/pages/Analysis/AnalysisPage.jsx

import { useMemo, useState } from "react";
import { getLang, getPatientTypesByLang, getTitlesByLang } from "../../lib/analysisApi";

export default function AnalysisPage() {
  const lang = getLang();
  const titles = useMemo(() => getTitlesByLang(lang), [lang]);
  const types = useMemo(() => getPatientTypesByLang(lang), [lang]);

  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState("");
  const [resultKey, setResultKey] = useState("");

  function classify(nextQ1, nextQ2, nextQ3) {
    const a1 = nextQ1 || q1;
    const a2 = nextQ2 || q2;
    const a3 = nextQ3 || q3;

    if (!a1 || !a2 || !a3) {
      setResultKey("");
      return;
    }

    // 레거시 점수 규칙 그대로
    const scores = {
      self_driven: 0,
      trigger_activated: 0,
      externally_anchored: 0,
      passive_dependent: 0,
      low_engagement: 0,
    };

    if (a1 === "self_driven") scores.self_driven += 3;
    if (a1 === "trigger_activated") scores.trigger_activated += 2;
    if (a1 === "externally_anchored") scores.externally_anchored += 3;

    if (a2 === "passive_dependent") {
      scores.passive_dependent += 2;
      scores.self_driven += 1;
      scores.externally_anchored += 1;
    }
    if (a2 === "low_engagement") scores.low_engagement += 3;

    if (a3 === "trigger_activated_confirm") {
      scores.trigger_activated += 2;
      scores.self_driven += 1;
    }
    if (a3 === "dependent_or_low") {
      scores.passive_dependent += 1;
      scores.low_engagement += 1;
      scores.externally_anchored += 1;
    }

    // 최댓값 선정
    let max = -1;
    let winner = "";
    Object.keys(scores).forEach((k) => {
      if (scores[k] > max) {
        max = scores[k];
        winner = k;
      }
    });

    // 레거시 특수 규칙
    if (a1 === "trigger_activated" && a3 === "trigger_activated_confirm") {
      winner = "trigger_activated";
    } else if (a2 === "passive_dependent" && a3 === "dependent_or_low") {
      winner = "passive_dependent";
    }

    setResultKey(winner);
  }

  function resetForm() {
    setQ1("");
    setQ2("");
    setQ3("");
    setResultKey("");
  }

  function renderResult() {
    if (!resultKey) return null;
    const t = types[resultKey];
    if (!t) return null;

    const tipsHtml = (t.tips || [])
      .map(
        (tip) =>
          `<li class="flex items-start"><svg class="w-5 h-5 mr-2 mt-1 text-pink-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg><span>${tip}</span></li>`
      )
      .join("");

    const html = `
      <div id="result-card" class="prose p-6 rounded-lg shadow-md border ${t.color} result-card-enter">
        <h3 class="sectionTitle font-bold ${t.textColor} !mt-0 !mb-2">${titles.resultTitlePrefix}${t.name}</h3>
        <p class="text-sm ${t.textColor}">${t.description}</p>
        <h4 class="font-semibold ${t.textColor} !mb-3">${titles.guideTitle}</h4>
        <ul class="space-y-2 text-sm ${t.textColor} !pl-0">
          ${tipsHtml}
        </ul>
      </div>
    `;
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  }

  return (
    <section className="analysis">
      {/* 공통 타이틀 블록 */}
      <div id="content_title">
        <h2>{titles.appTitle}</h2>
        <p>{titles.appSubtitle}</p>
      </div>

      <div className="content">
        {/* STEP 1 */}
        <div className="step mrb30">
          <div className="step-box">
            <h3 className="sectionTitle">{titles.step1Title}</h3>
            <p className="step-help">{titles.step1Desc}</p>

            <fieldset className="space-y-3">
              <legend className="question_label">{titles.step1Legend}</legend>

              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50 transition-colors">
                <input
                  type="radio"
                  name="q1"
                  value="self_driven"
                  checked={q1 === "self_driven"}
                  onChange={(e) => {
                    setQ1(e.target.value);
                    classify(e.target.value, undefined, undefined);
                  }}
                  className="custom-radio h-5 w-5 text-pink-500 border-slate-300 focus:ring-pink-500"
                />
                <span className="ml-4 text-slate-700">
                  "제 나름대로 기준이 있고, 지키려고 노력하는 편이에요."
                </span>
              </label>

              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50 transition-colors">
                <input
                  type="radio"
                  name="q1"
                  value="trigger_activated"
                  checked={q1 === "trigger_activated"}
                  onChange={(e) => {
                    setQ1(e.target.value);
                    classify(e.target.value, undefined, undefined);
                  }}
                  className="custom-radio h-5 w-5 text-pink-500 border-slate-300 focus:ring-pink-500"
                />
                <span className="ml-4 text-slate-700">
                  "너무 들쭉날쭉해요. 뭔가 하나라도 좀 잡아야 할 것 같아요."
                </span>
              </label>

              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50 transition-colors">
                <input
                  type="radio"
                  name="q1"
                  value="externally_anchored"
                  checked={q1 === "externally_anchored"}
                  onChange={(e) => {
                    setQ1(e.target.value);
                    classify(e.target.value, undefined, undefined);
                  }}
                  className="custom-radio h-5 w-5 text-pink-500 border-slate-300 focus:ring-pink-500"
                />
                <span className="ml-4 text-slate-700">
                  "혼자서는 잘 안돼요. 누가 계속 챙겨주고 시켜줘야 해요."
                </span>
              </label>
            </fieldset>
          </div>
        </div>

        {/* STEP 2 */}
        <div className="step mrb30">
          <div className="step-box">
            <h3 className="sectionTitle">{titles.step2Title}</h3>
            <p className="step-help">{titles.step2Desc}</p>

            <fieldset className="space-y-3">
              <legend className="question_label">{titles.step2Legend}</legend>

              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50 transition-colors">
                <input
                  type="radio"
                  name="q2"
                  value="passive_dependent"
                  checked={q2 === "passive_dependent"}
                  onChange={(e) => {
                    setQ2(e.target.value);
                    classify(undefined, e.target.value, undefined);
                  }}
                  className="custom-radio h-5 w-5 text-pink-500 border-slate-300 focus:ring-pink-500"
                />
                <span className="ml-4 text-slate-700">
                  "네, 좋아요. 그런 것도 꼭 알려주시면 도움이 될 것 같아요."
                </span>
              </label>

              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50 transition-colors">
                <input
                  type="radio"
                  name="q2"
                  value="low_engagement"
                  checked={q2 === "low_engagement"}
                  onChange={(e) => {
                    setQ2(e.target.value);
                    classify(undefined, e.target.value, undefined);
                  }}
                  className="custom-radio h-5 w-5 text-pink-500 border-slate-300 focus:ring-pink-500"
                />
                <span className="ml-4 text-slate-700">
                  "아니요, 괜찮아요. 일단 약으로만 효과를 보고 싶어요."
                </span>
              </label>
            </fieldset>
          </div>
        </div>

        {/* STEP 3 */}
        <div className="step">
          <div className="step-box">
            <h3 className="sectionTitle">{titles.step3Title}</h3>
            <p className="step-help">{titles.step3Desc}</p>

            <fieldset className="space-y-3">
              <legend className="question_label">{titles.step3Legend}</legend>

              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50 transition-colors">
                <input
                  type="radio"
                  name="q3"
                  value="trigger_activated_confirm"
                  checked={q3 === "trigger_activated_confirm"}
                  onChange={(e) => {
                    setQ3(e.target.value);
                    classify(undefined, undefined, e.target.value);
                  }}
                  className="custom-radio h-5 w-5 text-pink-500 border-slate-300 focus:ring-pink-500"
                />
                <span className="ml-4 text-slate-700">
                  "네, 하나 정도는 해볼 수 있을 것 같아요. 그게 뭐죠?"
                </span>
              </label>

              <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-pink-50 transition-colors">
                <input
                  type="radio"
                  name="q3"
                  value="dependent_or_low"
                  checked={q3 === "dependent_or_low"}
                  onChange={(e) => {
                    setQ3(e.target.value);
                    classify(undefined, undefined, e.target.value);
                  }}
                  className="custom-radio h-5 w-5 text-pink-500 border-slate-300 focus:ring-pink-500"
                />
                <span className="ml-4 text-slate-700">
                  "글쎄요... 그것도 좀 부담스럽거나 어려울 것 같아요."
                </span>
              </label>
            </fieldset>
          </div>
        </div>

        {/* 결과 영역 */}
        <div className="step_result">
          <div id="result-container">{renderResult()}</div>
        </div>

        {/* 리셋 */}
        <div className="step_reset" style={{ textAlign: "center" }}>
          <button
            id="reset-button"
            className="copy"
            onClick={(e) => {
              e.preventDefault();
              resetForm();
            }}
          >
            {titles.resetLabel}
          </button>
        </div>
      </div>
    </section>
  );
}
